import { useDefaultSalePricesStore } from "@fero/stores";
import type { ProductSalePriceInfo } from "@fero-shared/api-contracts";

export const useDefaultSalePricesHelpers = () => {
  const { salePricesToSave, setSalePricesToSave } = useDefaultSalePricesStore();

  /**
   * Checks if there are any prices currently being edited
   */
  const isEditingPrices = salePricesToSave.length > 0;

  /**
   * Checks if a specific product is being edited
   */
  const isEditingPrice = (productId: string) => {
    return salePricesToSave.some(
      (salePrice) => salePrice.productId === productId,
    );
  };

  /**
   * Starts editing a price for a specific product by adding it to the `salePricesToSave` array
   */
  const startEditingPrice = (productSalePriceInfo: ProductSalePriceInfo) => {
    if (!isEditingPrice(productSalePriceInfo.productId)) {
      setSalePricesToSave([...salePricesToSave, productSalePriceInfo]);
    }
  };

  /**
   * Cancels editing for a specific product by removing it from the `salePricesToSave` array
   */
  const cancelEditingPrice = (productId: string) => {
    setSalePricesToSave(
      salePricesToSave.filter((salePrice) => salePrice.productId !== productId),
    );
  };

  /**
   * Clears the "editing state" of sale prices by resetting the `salePricesToSave` array
   */
  const clearEditingPrices = () => {
    setSalePricesToSave([]);
  };

  /**
   * Updates the price for a specific product in the `salePricesToSave` array
   */
  const updatePriceToSave = (
    productId: string,
    updatedSalePrice: Partial<ProductSalePriceInfo>,
  ) => {
    const updatedPrices = salePricesToSave.map((salePrice) =>
      salePrice.productId === productId
        ? { ...salePrice, ...updatedSalePrice }
        : salePrice,
    );
    setSalePricesToSave(updatedPrices);
  };

  return {
    salePricesToSave,
    isEditingPrices,
    isEditingPrice,
    startEditingPrice,
    cancelEditingPrice,
    clearEditingPrices,
    updatePriceToSave,
  };
};
